import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useSpecies, useLoreLookup, LORE_LOOKUP_KEY } from '../api'
import { CloseButton, QuietButton, spinning } from '../design'
import { useLang } from '../lang'
import { useFollow } from '../favourites'
import { FollowButton } from './FollowButton'
import { AudioBar } from './AudioBar'
import { LoreCredit } from './LoreCredit'
import { Sparkline } from './Sparkline'
import { ago, elapsed, stamp } from '../time'
import type { EnrichmentBlock, LoreLookup } from '../types'
import * as m from './species.css'

function csrf(): string {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ''
}

// Keep the extreme anchor ticks inside the plot's edges; centre the rest on their mark —
// the same placement the Live sparkline's anchors use.
function shift(x: number): string {
  return x <= 0 ? '0' : x >= 1 ? '-100%' : '-50%'
}

// The BoCCI listing's reader-facing name. The server sends the bare colour; the Directory has
// always shown "Red list", and this sheet used to build the same string a second time.
function consLabel(name: string | null, t: (en: string, ga?: string) => string): string {
  return name ? t(`${name} list`, `liosta ${name}`) : ''
}

// The printer's-mark label for each block type, bilingual.
function loreLabel(type: EnrichmentBlock['type'], t: (en: string, ga?: string) => string): string {
  switch (type) {
    case 'folklore':
      return t('Folklore', 'Béaloideas')
    case 'regional_note':
      return t('In Ireland', 'In Éirinn')
    case 'station_reading':
      return t('At the station', 'Ag an stáisiún')
    default:
      return t('Fact', 'Fíric')
  }
}

// One enrichment block, verbatim from the bundle, with its citations as quiet host
// links — the card renders what Ruby sourced, it never re-derives. Shows the Irish
// rendering in GA when there is one, falling back to English.
function Lore({ kind, block, ga }: { kind: string; block: EnrichmentBlock; ga: boolean }) {
  const body = ga && block.text_ga ? block.text_ga : block.text
  return (
    <div className={m.loreItem}>
      <span className={m.loreK}>{kind}</span>
      {block.title && <span className={m.loreTitle}>{block.title}</span>}
      {body && <span className={m.loreText}>{body}</span>}
      {block.quote && <span className={m.loreVerse}>{block.quote}</span>}
      {block.note && <span className={m.loreNote}>{block.note}</span>}
      {/* A curated entry composes its own book credit; a scraped block links its host source(s). */}
      {block.credit ? (
        <span className={m.loreCredit}>{block.credit}</span>
      ) : block.sources.length > 0 ? (
        <span className={m.loreSrc}>
          {block.sources.map((s, i) => (
            <LoreCredit key={i} source={s} />
          ))}
        </span>
      ) : null}
    </div>
  )
}

// The species-detail overlay. Data from /api/species/:sci. Header carries the follow
// checkbox (left) and the EN|GA toggle + close (right); the sheet closes with the
// station's own signature. The #detail-modal id + is-open class are KEPT so the legacy
// refresh controller still skips auto-refresh while the sheet is open.
export function SpeciesModal({ sci, onClose }: { sci: string; onClose: () => void }) {
  const { data } = useSpecies(sci)
  const { lang, setLang, t } = useLang()
  const { enabled: signedIn } = useFollow()
  const qc = useQueryClient()
  // The on-demand look-up. Sourcing a bird's folklore is minutes of work in a background job,
  // so the click only STARTS it (`starting`) and the card then follows it by polling — `asked`
  // is what turns that poll on, and the server's status is what turns it off. `failed` is a
  // request that never landed (offline, a stale CSRF token), which is a different thing from a
  // run that honestly found nothing, and reads differently on the button.
  const [asked, setAsked] = useState(false)
  const [starting, setStarting] = useState(false)
  const [failed, setFailed] = useState(false)
  const { data: cached } = useLoreLookup(sci, asked)
  // Only ever read a state this card asked for. React-query keeps the last one cached, and the
  // card is unmounted when the sheet closes — so a reopen would otherwise read back a stale
  // 'working' and spin against a poll that is switched off. Asking again picks the run back up
  // (the server refuses to start a second one), which is the honest way to rejoin it.
  const look = asked ? cached : undefined

  // The bird on screen right now, for the in-flight start below: the card is one component for
  // every bird, so a reply that arrives after the reader has moved on must be dropped rather
  // than shown under the wrong name.
  const shown = useRef(sci)

  // Reset the look-up when the card switches to a different bird (same component).
  useEffect(() => {
    shown.current = sci
    setAsked(false)
    setStarting(false)
    setFailed(false)
  }, [sci])

  // Escape closes; the sheet takes focus when it opens and hands it back to whatever opened it
  // when it closes, so a keyboard reader is not dropped at the top of the page behind it.
  const dialogRef = useRef<HTMLElement>(null)
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null
    const onEsc = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    document.addEventListener('keydown', onEsc)
    document.body.classList.add('modal-open')
    dialogRef.current?.focus()
    return () => {
      document.removeEventListener('keydown', onEsc)
      document.body.classList.remove('modal-open')
      opener?.focus?.()
    }
  }, [onClose])

  // Ask the server to start sourcing this bird's folklore (authed). It answers at once — the
  // run happens in a job — and the answer is the poll's first state, so seed the query with it
  // BEFORE turning the poll on: enabling the query first would race the POST, and a `none` read
  // a moment too early could land on top of the `working` the start just reported.
  const lookUp = () => {
    setStarting(true)
    setFailed(false)
    fetch(`/species/${encodeURIComponent(sci)}/enrichment`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': csrf() },
    })
      .then((r) => (r.ok ? (r.json() as Promise<LoreLookup>) : Promise.reject(r.status)))
      .then((state) => {
        if (shown.current !== sci) return
        qc.setQueryData(LORE_LOOKUP_KEY(sci), state)
        setAsked(true)
      })
      .catch(() => {
        if (shown.current === sci) setFailed(true)
      })
      .finally(() => {
        if (shown.current === sci) setStarting(false)
      })
  }

  const name = data ? (lang === 'ga' && data.ga ? data.ga : data.en) : ''
  // The other language, shown as the subtitle (Irish under English, or vice versa).
  const subtitle = data ? (lang === 'ga' ? data.en : data.ga) : null
  const desc = data ? (lang === 'ga' ? data.description_ga || data.description : data.description) : null
  const cons = data?.conservation
  // Folklore only. The bundle also carries `fact` / `regional_note` blocks, but those are raw
  // material for the narrated day — the Journal and the letter turn them into prose — not
  // something to list at the reader as cited bullets. Here the description above already says
  // what the bird is; what earns its place after it is the folklore.
  // The look-up's result WINS over what the card was opened with — that is the whole point of
  // having asked. (The other way round, a bird whose stored bundle holds facts but no folklore
  // pinned the card to that bundle for ever, and the button did nothing however often it was
  // pressed: the answer arrived and was thrown away.)
  const enr = look?.enrichment ?? data?.enrichment
  const folk = enr?.blocks.filter((b) => b.type === 'folklore') ?? []
  // A run under way — the start's own request, or a run the server says is still going.
  const looking = starting || look?.status === 'working'
  // Looked today and found none. Saying so, and stopping, is honest: the server won't re-source
  // the same bird again today, so another press would be the no-op this button was known for.
  const barren = !failed && look?.status === 'none'

  return (
    <div id="detail-modal" className={`${m.overlay} is-open`}>
      <div className={m.backdrop} onClick={onClose} />
      <article role="dialog" aria-modal="true" tabIndex={-1} ref={dialogRef} className={m.card}>
        {data && (
          <>
            <div className={m.head}>
              <FollowButton sci={data.sci} variant="full" />
              <div className={m.headRight}>
                <div className={m.lang} role="group" aria-label="Language">
                  <button
                    className={lang === 'en' ? m.langOpt.on : m.langOpt.off}
                    onClick={() => setLang('en')}
                  >
                    EN
                  </button>
                  <button
                    className={lang === 'ga' ? m.langOpt.on : m.langOpt.off}
                    onClick={() => setLang('ga')}
                  >
                    GA
                  </button>
                </div>
                <CloseButton aria-label={t('Close', 'Dún')} onClick={onClose} />
              </div>
            </div>

            <div className={m.grid}>
              <div className={m.img}>
                {data.illustrations.map((img) => (
                  <img key={img.label} src={img.url} alt={`${data.en} (${img.label})`} loading="eager" />
                ))}
              </div>
              <div className={m.info}>
                {cons?.status && (
                  <div className={m.consLine}>
                    <span className={m.consDot[cons.status]} />
                    <span className={m.consName}>{consLabel(cons.name, t)}</span>
                    <span className={m.consNote}>{cons.note}</span>
                  </div>
                )}
                <h2 className={m.title}>{name}</h2>
                {subtitle && subtitle !== name && <p className={m.common}>{subtitle}</p>}
                <p className={m.sci}>{data.sci}</p>
                <div className={m.stats}>
                  <div>
                    <span className={m.statN}>{data.all_time.toLocaleString()}</span>
                    <span className={m.statLbl}>{t('all time', 'riamh')}</span>
                  </div>
                  <div>
                    <span className={m.statN}>{data.today.toLocaleString()}</span>
                    <span className={m.statLbl}>{t('today', 'inniu')}</span>
                  </div>
                  <div>
                    <span className={m.statN}>{elapsed(data.first_seen, lang)}</span>
                    <span className={m.statLbl}>{t('first heard', 'chéad chloiste')}</span>
                  </div>
                </div>
                {desc && <p className={m.desc}>{desc}</p>}
                {/* The call is core functionality — it sits above the folklore, not below it. */}
                {data.song && (
                  <div className={m.audio}>
                    <span className={m.audioLabel}>{t('Listen to the call', 'Éist leis an nglaoch')}</span>
                    <AudioBar src={data.song} />
                  </div>
                )}
                {folk.length > 0 ? (
                  <div className={m.lore}>
                    {folk.map((b, i) => (
                      <Lore key={i} kind={loreLabel(b.type, t)} block={b} ga={lang === 'ga'} />
                    ))}
                  </div>
                ) : signedIn ? (
                  <QuietButton
                    className={m.lookup}
                    onClick={lookUp}
                    disabled={looking || barren}
                    aria-busy={looking}
                  >
                    <i
                      className={`ti ${looking ? `ti-loader ${spinning}` : 'ti-books'}`}
                      aria-hidden="true"
                    />
                    {looking
                      ? t('Looking…', 'Ag cuardach…')
                      : failed
                        ? t('Look-up failed — try again', 'Theip ar an gcuardach — féach arís')
                        : barren
                          ? t('No folklore found yet', 'Faic fós')
                          : t('Look up folklore', 'Cuardaigh béaloideas')}
                  </QuietButton>
                ) : null}
              </div>
            </div>

            {/* The bird's whole run here, drawn as a stilled by-date sparkline — resident,
                one-off, or seasonal returner at a glance, the thing the counters above can't
                show. Dropped for a bird with under two days of history (server returns null),
                the same absence-not-furniture discipline the Live line follows. The date
                anchors are positioned/shifted exactly as the Live sparkline's. */}
            {data.history && (
              <div className={m.section}>
                <div className={m.recHead}>
                  <h3 className={m.recTitle}>{t('At the station', 'Ag an stáisiún')}</h3>
                  <span className={m.recCount}>
                    {t(`over ${data.history.days} days`, `thar ${data.history.days} lá`)}
                  </span>
                </div>
                <Sparkline paths={data.history.sparkline} muted />
                <div className={m.anchors}>
                  {data.history.anchors.map((a, i) => (
                    <span key={i} style={{ left: `${a.x * 100}%`, transform: `translateX(${shift(a.x)})` }}>
                      {lang === 'ga' ? a.ga : a.en}
                    </span>
                  ))}
                </div>
                {/* The run read back in a sentence or two — composed from the counts, not a model. */}
                <p className={m.historyNote}>{data.history.description}</p>
              </div>
            )}

            {/* Detections only for a bird actually heard here — an un-heard species (browsable
                from the "all species" directory, but never recorded) has an empty roll, so the
                table is dropped rather than shown as a bare, zeroed header. */}
            {data.recent.length > 0 && (
              <div className={m.section}>
                <div className={m.recHead}>
                  <h3 className={m.recTitle}>{t('Detections', 'Aimsithe')}</h3>
                  <span className={m.recCount}>
                    {t('most recent', 'is déanaí')} {data.recent.length}
                  </span>
                </div>
                <ol className={m.roll}>
                  {data.recent.map((r, i) => (
                    <li key={i}>
                      <span className={m.recWhen}>
                        {ago(r.at, lang)}
                        <small>{stamp(r.at, lang)}</small>
                      </span>
                      <span className={m.recConf}>{Math.round(r.confidence * 100)}%</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </>
        )}
      </article>
    </div>
  )
}
